import { createFetchCacheIDB } from './utils'

const ITUNES_SEARCH_URL = 'https://itunes.apple.com/search'
const LRCLIB_SEARCH_URL = 'https://lrclib.net/api/search'
const ZHCONVERT_CONVERT_URL = 'https://api.zhconvert.org/convert'
const TRACK_CACHE = new Map()
const LYRICS_CACHE = new Map()
const ARTIST_ARTWORK_CACHE = new Map()
const FETCH_CACHE_IDB = createFetchCacheIDB()
const TRACK_CACHE_PREFIX = 'metadata:v2:'
const ARTIST_ARTWORK_CACHE_PREFIX = 'artist-artwork:v2:'
const LYRICS_CACHE_PREFIX = 'lyrics:v4:'
const LEGACY_TRACK_CACHE_STORAGE_KEY = '--track-metadata-cache-v1--'
const LEGACY_LYRICS_CACHE_STORAGE_KEY = '--track-lyrics-cache-v1--'

cleanupLegacyCacheStorage()

export async function fetchTrackMetadata(fileName, signal) {
  const parsed = parseTrackFileName(fileName)
  const cacheKey = getTrackCacheKey(parsed)

  if (TRACK_CACHE.has(cacheKey)) {
    return TRACK_CACHE.get(cacheKey)
  }

  const cachedMetadata = await getCachedValue(cacheKey)
  if (cachedMetadata) {
    TRACK_CACHE.set(cacheKey, cachedMetadata)
    return cachedMetadata
  }

  const metadata = {
    title: parsed.title,
    artist: parsed.artist,
    artistId: '',
    songImage: '',
    artistImage: '',
    source: '',
  }

  try {
    const track = await fetchItunesTrack(parsed, signal)
    if (track) {
      metadata.artistId = track.artistId || ''
      metadata.songImage = getLargeArtwork(track.artworkUrl100)
      metadata.source = 'Apple Music'
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw error
    }
  }

  TRACK_CACHE.set(cacheKey, metadata)
  await setCachedValue(cacheKey, metadata)
  return metadata
}

export async function fetchArtistArtworkImages(
  artist,
  signal,
  limit = 50,
  artistId = ''
) {
  const normalizedArtist = normalizeValue(artist)
  const normalizedArtistId = String(artistId || '').trim()
  if (!normalizedArtist && !normalizedArtistId) {
    return []
  }

  const cacheKey = `${ARTIST_ARTWORK_CACHE_PREFIX}${
    normalizedArtistId || normalizedArtist
  }`
  if (ARTIST_ARTWORK_CACHE.has(cacheKey)) {
    return ARTIST_ARTWORK_CACHE.get(cacheKey)
  }

  const cachedImages = await getCachedValue(cacheKey)
  if (Array.isArray(cachedImages)) {
    ARTIST_ARTWORK_CACHE.set(cacheKey, cachedImages)
    return cachedImages
  }

  try {
    const albums = normalizedArtistId
      ? await fetchItunesArtistAlbumsById(normalizedArtistId, signal)
      : await fetchItunesArtistAlbumsByName(artist, signal)
    const seenImages = new Set()
    const images = albums.reduce((result, album) => {
      const albumArtist = normalizeValue(album.artistName)
      const albumArtistId = String(album.artistId || '').trim()
      const artworkUrl = getLargeArtwork(album.artworkUrl100)

      if (
        (albumArtistId === normalizedArtistId ||
          albumArtist === normalizedArtist) &&
        artworkUrl &&
        !seenImages.has(artworkUrl)
      ) {
        seenImages.add(artworkUrl)
        result.push(artworkUrl)
      }

      return result
    }, [])

    const limitedImages = images.slice(0, limit)
    ARTIST_ARTWORK_CACHE.set(cacheKey, limitedImages)
    await setCachedValue(cacheKey, limitedImages)
    return limitedImages
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error
    }

    ARTIST_ARTWORK_CACHE.set(cacheKey, [])
    await setCachedValue(cacheKey, [])
    return []
  }
}

export function parseTrackFileName(fileName = '') {
  const cleanName = fileName
    .replace(/\.(mp3|wav|ogg|webm)$/i, '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const parts = cleanName.split(/\s*-\s*/)
  if (parts.length >= 2) {
    const artist = parts[0].trim()
    const title = parts.slice(1).join(' - ').trim()

    if (!artist || !title) {
      return {
        artist: '',
        title: cleanName || 'Unknown Track',
      }
    }

    return {
      artist,
      title,
    }
  }

  return {
    artist: '',
    title: cleanName || 'Unknown Track',
  }
}

export async function fetchTrackLyrics(fileName, signal) {
  const parsed = parseTrackFileName(fileName)
  const cacheKey = getLyricsCacheKey(parsed)

  if (LYRICS_CACHE.has(cacheKey)) {
    return LYRICS_CACHE.get(cacheKey)
  }

  const cachedLyrics = await getCachedValue(cacheKey)
  if (typeof cachedLyrics === 'string') {
    LYRICS_CACHE.set(cacheKey, cachedLyrics)
    return cachedLyrics
  }

  if (!parsed.artist || !parsed.title) {
    LYRICS_CACHE.set(cacheKey, '')
    await setCachedValue(cacheKey, '')
    return ''
  }

  try {
    const result = await fetchLrclibLyrics(parsed, signal)
    const lyrics = result?.syncedLyrics || result?.plainLyrics || ''
    const displayLyrics = await getDisplayLyrics(lyrics, signal)
    LYRICS_CACHE.set(cacheKey, displayLyrics)
    await setCachedValue(cacheKey, displayLyrics)
    return displayLyrics
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error
    }

    LYRICS_CACHE.set(cacheKey, '')
    await setCachedValue(cacheKey, '')
    return ''
  }
}

async function getDisplayLyrics(lyrics, signal) {
  if (!lyrics) {
    return ''
  }

  if (!looksLikeTraditionalChineseLyrics(lyrics)) {
    return lyrics
  }

  try {
    const { protectedLyrics, lyricTags } = protectLyricTags(lyrics)
    const simplifiedLyrics = await fetchSimplifiedChineseLyrics(
      protectedLyrics,
      lyricTags,
      signal
    )
    return simplifiedLyrics || lyrics
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error
    }

    return lyrics
  }
}

async function fetchItunesTrack(parsed, signal) {
  const query = [parsed.artist, parsed.title].filter(Boolean).join(' ')
  if (!query) {
    return null
  }

  const url = new URL(ITUNES_SEARCH_URL)
  url.searchParams.set('term', query)
  url.searchParams.set('media', 'music')
  url.searchParams.set('entity', 'song')
  url.searchParams.set('limit', '1')

  const response = await fetch(url.toString(), { signal })
  if (!response.ok) {
    return null
  }

  const data = await response.json()
  return data.results?.[0] || null
}

async function fetchItunesArtistAlbumsById(artistId, signal) {
  const url = new URL('https://itunes.apple.com/lookup')
  url.searchParams.set('id', artistId)
  url.searchParams.set('entity', 'album')
  url.searchParams.set('limit', '200')
  url.searchParams.set('country', 'US')

  const response = await fetch(url.toString(), { signal })
  if (!response.ok) {
    return []
  }

  const data = await response.json()
  return Array.isArray(data.results)
    ? data.results.filter((item) => item.wrapperType === 'collection')
    : []
}

async function fetchItunesArtistAlbumsByName(artist, signal) {
  const url = new URL(ITUNES_SEARCH_URL)
  url.searchParams.set('term', artist)
  url.searchParams.set('media', 'music')
  url.searchParams.set('entity', 'album')
  url.searchParams.set('attribute', 'artistTerm')
  url.searchParams.set('limit', '200')

  const response = await fetch(url.toString(), { signal })
  if (!response.ok) {
    return []
  }

  const data = await response.json()
  return Array.isArray(data.results) ? data.results : []
}

async function fetchLrclibLyrics(parsed, signal) {
  const url = new URL(LRCLIB_SEARCH_URL)
  url.searchParams.set('track_name', parsed.title)
  url.searchParams.set('artist_name', parsed.artist)

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
    },
    signal,
  })

  if (!response.ok) {
    return null
  }

  const results = await response.json()
  if (!Array.isArray(results) || !results.length) {
    return null
  }

  return pickBestLyricsResult(results, parsed)
}

async function fetchSimplifiedChineseLyrics(lyrics, lyricTags, signal) {
  const body = new URLSearchParams({
    converter: 'Simplified',
    outputFormat: 'json',
    text: lyrics,
  })

  const response = await fetch(ZHCONVERT_CONVERT_URL, {
    body,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    method: 'POST',
    signal,
  })

  if (!response.ok) {
    return ''
  }

  const result = await response.json()
  if (typeof result?.data?.text !== 'string') {
    return ''
  }

  return restoreLyricTags(result.data.text, lyricTags)
}

const LYRIC_TAG_REGEXP = /\[[^\]]+\]/g
const LYRIC_TAG_TOKEN_REGEXP = /CODEX_LYRIC_TAG_(\d+)_TOKEN/g

function protectLyricTags(lyrics = '') {
  const lyricTags = []
  const protectedLyrics = lyrics.replace(LYRIC_TAG_REGEXP, (tag) => {
    const index = lyricTags.push(tag) - 1
    return `CODEX_LYRIC_TAG_${index}_TOKEN`
  })

  return {
    protectedLyrics,
    lyricTags,
  }
}

function restoreLyricTags(lyrics = '', lyricTags = []) {
  return lyrics.replace(LYRIC_TAG_TOKEN_REGEXP, (_, index) => {
    return lyricTags[Number(index)] || ''
  })
}

function isExactLyricsMatch(item, parsed) {
  return (
    normalizeValue(item.trackName) === normalizeValue(parsed.title) &&
    normalizeValue(item.artistName).includes(normalizeValue(parsed.artist))
  )
}

function pickBestLyricsResult(results, parsed) {
  const exactSyncedMatch = results.find(
    (item) => isExactLyricsMatch(item, parsed) && item.syncedLyrics
  )
  if (exactSyncedMatch) {
    return exactSyncedMatch
  }

  const anySyncedMatch = results.find((item) => item.syncedLyrics)
  if (anySyncedMatch) {
    return anySyncedMatch
  }

  const exactPlainMatch = results.find(
    (item) => isExactLyricsMatch(item, parsed) && item.plainLyrics
  )
  if (exactPlainMatch) {
    return exactPlainMatch
  }

  return results.find((item) => item.plainLyrics) || null
}

function looksLikeTraditionalChineseLyrics(value = '') {
  return /[萬與專業東兩個為麼來們會兒區後從愛時樣發著這過還關讓話說點開門電麵體國臺灣樂]/.test(
    value
  )
}

function normalizeValue(value = '') {
  return value
    .toLowerCase()
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function getLargeArtwork(url = '') {
  return url.replace(/100x100bb\.(jpg|png|webp)$/i, '600x600bb.$1')
}

async function getCachedValue(cacheKey) {
  try {
    const record = await FETCH_CACHE_IDB.getData(cacheKey)
    return record?.value
  } catch (error) {
    return undefined
  }
}

async function setCachedValue(cacheKey, value) {
  try {
    await FETCH_CACHE_IDB.setData({
      key: cacheKey,
      value,
      updatedAt: Date.now(),
    })
  } catch (error) {
    // Ignore cache write failures so network fetches continue to work.
  }
}

function getTrackCacheKey(parsed) {
  return `${TRACK_CACHE_PREFIX}${getNormalizedTrackKey(parsed)}`
}

function getLyricsCacheKey(parsed) {
  return `${LYRICS_CACHE_PREFIX}${getNormalizedTrackKey(parsed)}`
}

function getNormalizedTrackKey(parsed) {
  return `${parsed.artist}|${parsed.title}`.toLowerCase()
}

function cleanupLegacyCacheStorage() {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.removeItem(LEGACY_TRACK_CACHE_STORAGE_KEY)
    localStorage.removeItem(LEGACY_LYRICS_CACHE_STORAGE_KEY)
  } catch (error) {
    // Ignore cleanup failures so playback continues even if old cache keys remain.
  }
}
